/**
 * Helpers for inserting text into a controlled <textarea> / <input> at the
 * caret: emoji picker, note templates, and template variable badges.
 */

export interface InsertAtCaretResult {
  /** The new field value, with `insertion` spliced in. */
  content: string
  /** Where the caret should sit afterwards (just past the insertion). */
  caret: number
}

/**
 * Splice `insertion` into `value` at the element's current caret position.
 *
 * If the user has a selection, the insertion replaces it — matching what
 * typing a character would do. When the element isn't available (not yet
 * mounted, or the picker was opened without the field ever being focused) we
 * fall back to appending at the end.
 */
export const insertAtCaret = (
  element: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  insertion: string
): InsertAtCaretResult => {
  const safeValue = value ?? ''

  // selectionStart/End are null for input types that don't support selection.
  const start = element?.selectionStart ?? safeValue.length
  const end = element?.selectionEnd ?? start

  return {
    content: safeValue.slice(0, start) + insertion + safeValue.slice(end),
    caret: start + insertion.length,
  }
}

/**
 * Restore focus and place the caret after a programmatic insertion.
 *
 * Deferred so it runs after React has flushed the new value to the DOM —
 * setting selectionRange against the stale value would put the caret in the
 * wrong place (or get clobbered by the re-render). setTimeout rather than
 * requestAnimationFrame: rAF doesn't fire while a document is hidden, which
 * would leave the caret unrestored in a backgrounded tab.
 */
export const focusCaretAfterInsert = (
  element: HTMLTextAreaElement | HTMLInputElement | null,
  caret: number
) => {
  if (!element) return

  setTimeout(() => {
    element.focus()
    element.setSelectionRange(caret, caret)
  }, 0)
}
